import React from 'react'

// Silhouette color — darker than the water at every depth, so creatures read
// as shapes in the water column and fade the deeper they swim.
const INK = '#00131f'

const Fish = () => (
  <svg viewBox="0 0 100 48" width="100%" style={{display: 'block'}}>
    <g fill={INK}>
      <ellipse cx="42" cy="24" rx="30" ry="13"/>
      <path d="M66 24 L94 8 Q87 24 94 40 Z"/>
      <path d="M28 13 Q40 1 54 13 Z"/>
      <path d="M30 35 Q40 46 52 35 Z"/>
    </g>
    <circle cx="22" cy="20" r="2.5" fill="rgba(210,235,255,0.55)"/>
  </svg>
)

const Shark = () => (
  <svg viewBox="0 0 220 100" width="100%" style={{display: 'block'}}>
    <g fill={INK}>
      <path d="M8 52 C30 30 84 22 130 26 C160 29 186 38 204 50 C186 62 156 72 122 74 C80 76 34 68 8 52 Z"/>
      <path d="M96 27 C100 10 116 4 126 6 C118 16 113 24 111 29 Z"/>
      <path d="M196 46 C204 28 214 16 220 12 C216 28 216 40 220 52 C214 64 206 74 198 80 C202 66 200 54 196 46 Z"/>
      <path d="M84 66 C90 84 100 92 108 94 C104 82 104 72 106 64 Z"/>
    </g>
    <path d="M62 42 q4 9 0 18 M71 41 q4 9 0 18 M80 40 q4 9 0 18"
      stroke="rgba(0,0,0,0.35)" strokeWidth="2" fill="none" strokeLinecap="round"/>
    <circle cx="30" cy="45" r="3" fill="rgba(210,235,255,0.5)"/>
  </svg>
)

const Jelly = () => (
  <svg viewBox="0 0 80 115" width="100%" style={{display: 'block'}}>
    <path d="M6 44 C6 18 22 6 40 6 C58 6 74 18 74 44 C64 50 52 52 40 52 C28 52 16 50 6 44 Z"
      fill="rgba(140,190,225,0.28)"/>
    <g stroke="rgba(140,190,225,0.35)" strokeWidth="2.5" fill="none" strokeLinecap="round">
      <path d="M18 52 q7 14 -2 27 q-7 13 2 27"/>
      <path d="M33 54 q-6 15 2 29 q6 13 -2 26"/>
      <path d="M48 54 q7 15 -1 29 q-6 13 2 26"/>
      <path d="M62 52 q-6 14 2 27 q6 13 -2 27"/>
    </g>
  </svg>
)

const School = () => (
  <svg viewBox="0 0 160 90" width="100%" style={{display: 'block'}}>
    <g fill={INK}>
      {[[10, 12], [46, 2], [86, 16], [34, 44], [76, 56], [118, 40]].map(([x, y], i) => (
        <g key={i} transform={`translate(${x} ${y})`}>
          <ellipse cx="14" cy="8" rx="12" ry="5.5"/>
          <path d="M23 8 L36 1 Q33 8 36 15 Z"/>
        </g>
      ))}
    </g>
  </svg>
)

// One entry per creature: vertical position (percent of the water column),
// horizontal travel direction, size, lap duration. Negative delays start each
// swimmer mid-lap so the water is populated on first paint.
const SWIMMERS = [
  {C: Shark, top: '3%', size: 240, dur: 95, delay: -40, dir: 'r', opacity: 0.5},
  {C: Fish, top: '7%', size: 72, dur: 38, delay: -10, dir: 'l', opacity: 0.5},
  {C: Fish, top: '10%', size: 56, dur: 34, delay: -22, dir: 'r', opacity: 0.48},
  {C: School, top: '12%', size: 150, dur: 58, delay: -25, dir: 'r', opacity: 0.45},
  {C: Fish, top: '17%', size: 46, dur: 30, delay: -5, dir: 'r', opacity: 0.42},
  {C: Jelly, top: '21%', size: 54, left: '12%', opacity: 0.4},
  {C: Fish, top: '24%', size: 66, dur: 40, delay: -8, dir: 'l', opacity: 0.42},
  {C: Fish, top: '28%', size: 92, dur: 46, delay: -20, dir: 'l', opacity: 0.45},
  {C: School, top: '32%', size: 170, dur: 66, delay: -40, dir: 'r', opacity: 0.4},
  {C: Shark, top: '37%', size: 310, dur: 115, delay: -70, dir: 'l', opacity: 0.4},
  {C: Fish, top: '41%', size: 50, dur: 33, delay: -15, dir: 'l', opacity: 0.38},
  {C: Jelly, top: '46%', size: 70, left: '78%', opacity: 0.32},
  {C: Fish, top: '49%', size: 78, dur: 42, delay: -28, dir: 'r', opacity: 0.36},
  {C: Fish, top: '54%', size: 60, dur: 36, delay: -12, dir: 'r', opacity: 0.35},
  {C: Shark, top: '58%', size: 260, dur: 100, delay: -55, dir: 'r', opacity: 0.3},
  {C: School, top: '64%', size: 130, dur: 62, delay: -35, dir: 'l', opacity: 0.3},
  {C: Fish, top: '69%', size: 64, dur: 38, delay: -10, dir: 'l', opacity: 0.3},
  {C: Fish, top: '74%', size: 82, dur: 50, delay: -18, dir: 'r', opacity: 0.28},
  {C: Fish, top: '81%', size: 90, dur: 54, delay: -30, dir: 'l', opacity: 0.25},
]

const Underwater = () => (
  <div className="uw-layer" aria-hidden="true">
    <style>{`
      .uw-layer {
        position: absolute;
        top: 70vh; bottom: 0; left: 0; right: 0;
        z-index: -1;
        overflow: hidden;
        pointer-events: none;
      }
      .uw-swimmer {
        position: absolute;
        left: 0;
      }
      .uw-r { animation-name: uw-swim-r; animation-timing-function: linear; animation-iteration-count: infinite; }
      .uw-l { animation-name: uw-swim-l; animation-timing-function: linear; animation-iteration-count: infinite; }
      .uw-r svg { transform: scaleX(-1); }
      .uw-bob { animation: uw-bob 7s ease-in-out infinite alternate; }
      @keyframes uw-swim-r { from { transform: translateX(-30vw); } to { transform: translateX(115vw); } }
      @keyframes uw-swim-l { from { transform: translateX(115vw); } to { transform: translateX(-30vw); } }
      @keyframes uw-bob { from { transform: translateY(0); } to { transform: translateY(-26px); } }
      @media (prefers-reduced-motion: reduce) {
        .uw-layer { display: none; }
      }
    `}</style>
    {SWIMMERS.map(({C, top, size, dur, delay, dir, left, opacity}, i) => (
      <div
        key={i}
        className={`uw-swimmer ${dir ? (dir === 'r' ? 'uw-r' : 'uw-l') : 'uw-bob'}`}
        style={{
          top,
          left,
          width: size,
          opacity,
          animationDuration: dur ? `${dur}s` : undefined,
          animationDelay: delay ? `${delay}s` : undefined,
        }}>
        <C />
      </div>
    ))}
  </div>
)

export default Underwater
